/**
 * Forward migration of play-debug defaults. A v1 file is the first shipped shape; v2 adds
 * camera `fitBlend` and `smoothnessIn`; v3 adds `aimBias`; v4 adds `placeZoom` and ships a
 * lower rest zoom; v5–v11 carried look knobs (aim chrome, waterline, flock) that now live on
 * `MapDocument.look` — v12 drops them and adds `nearMissPx` (screen-pixel gate near-miss
 * band; world = px / zoom). v13 adds squared-wind feel knobs and the provisional wind-ghost
 * overlay. Missing feel fields take the numbers the previous build hard-coded in TypeScript.
 */

import {
  CRASH_HARSHNESS,
  CRASH_HARSHNESS_MAX,
  CRASH_HARSHNESS_MIN,
  MOVEMENT_RADIUS,
  MOVEMENT_RADIUS_MAX,
  MOVEMENT_RADIUS_MIN,
  STICK_OVERFLOW_PX,
  STICK_OVERFLOW_PX_MIN,
  TRAIL_LENGTH_DEFAULT_M,
  TRAIL_LENGTH_MAX_M,
  TRAIL_LENGTH_MIN_M,
  TRAIL_SPEED_GAIN_DEFAULT,
  TRAIL_SPEED_GAIN_MAX,
  TRAIL_SPEED_GAIN_MIN,
  TURN_TAX,
  TURN_TAX_MAX,
  TURN_TAX_MIN,
  pixelsToMeters,
} from '../constants.ts';
import {
  DEFAULT_WIND_TUNING,
  WIND_COEFFICIENT_MAX,
  WIND_COEFFICIENT_MIN,
  WIND_EXPOSURE_MAX,
  WIND_EXPOSURE_MIN,
  WIND_LOW_SPEED_MAX,
  WIND_LOW_SPEED_MIN,
  WIND_OFFSET_CAP_MAX_M,
  WIND_OFFSET_CAP_MIN_M,
} from '../environment/wind.ts';
import { clamp } from '../math.ts';
import { CURVE_KINDS, DEFAULT_CURVE_KIND, type CurveKind } from '../path.ts';
import { NEAR_MISS_PX, NEAR_MISS_PX_MAX, NEAR_MISS_PX_MIN } from '../race.ts';
import { DEFAULT_PLAY_CAMERA } from './camera.ts';
import type {
  DebugDefaultsCamera,
  DebugDefaultsDocument,
  DebugDefaultsLines,
  DebugDefaultsWind,
} from './types.ts';
import {
  CURRENT_DEBUG_DEFAULTS_VERSION,
  OLDEST_SUPPORTED_DEBUG_DEFAULTS_VERSION,
} from './version.ts';

export { CURRENT_DEBUG_DEFAULTS_VERSION, OLDEST_SUPPORTED_DEBUG_DEFAULTS_VERSION };

const CURVE_KIND_SET = new Set<string>(CURVE_KINDS.map(({ kind }) => kind));

const DEFAULT_CAMERA: DebugDefaultsCamera = { ...DEFAULT_PLAY_CAMERA };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function asFiniteNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function asBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function asCurveKind(value: unknown): CurveKind {
  return typeof value === 'string' && CURVE_KIND_SET.has(value)
    ? (value as CurveKind)
    : DEFAULT_CURVE_KIND;
}

function migrateLines(value: unknown): DebugDefaultsLines {
  if (!isRecord(value)) {
    return { trail: true, moveLine: true, prediction: true, windGhost: false };
  }

  return {
    trail: asBoolean(value.trail, true),
    moveLine: asBoolean(value.moveLine, true),
    prediction: asBoolean(value.prediction, true),
    windGhost: asBoolean(value.windGhost, false),
  };
}

function migrateWind(value: unknown): DebugDefaultsWind {
  if (!isRecord(value)) {
    return { ...DEFAULT_WIND_TUNING };
  }

  return {
    coefficient: clamp(
      asFiniteNumber(value.coefficient, DEFAULT_WIND_TUNING.coefficient),
      WIND_COEFFICIENT_MIN,
      WIND_COEFFICIENT_MAX,
    ),
    exposure: clamp(
      asFiniteNumber(value.exposure, DEFAULT_WIND_TUNING.exposure),
      WIND_EXPOSURE_MIN,
      WIND_EXPOSURE_MAX,
    ),
    lowSpeedMultiplier: clamp(
      asFiniteNumber(value.lowSpeedMultiplier, DEFAULT_WIND_TUNING.lowSpeedMultiplier),
      WIND_LOW_SPEED_MIN,
      WIND_LOW_SPEED_MAX,
    ),
    offsetCapM: clamp(
      asFiniteNumber(value.offsetCapM, DEFAULT_WIND_TUNING.offsetCapM),
      WIND_OFFSET_CAP_MIN_M,
      WIND_OFFSET_CAP_MAX_M,
    ),
  };
}

function migrateCamera(value: unknown): DebugDefaultsCamera {
  if (!isRecord(value)) {
    return { ...DEFAULT_CAMERA };
  }

  const smoothness = asFiniteNumber(value.smoothness, DEFAULT_CAMERA.smoothness);

  return {
    enabled: asBoolean(value.enabled, DEFAULT_CAMERA.enabled),
    placeZoom: asFiniteNumber(value.placeZoom, DEFAULT_CAMERA.placeZoom),
    restZoom: asFiniteNumber(value.restZoom, DEFAULT_CAMERA.restZoom),
    fastZoom: asFiniteNumber(value.fastZoom, DEFAULT_CAMERA.fastZoom),
    speedSensitivity: asFiniteNumber(
      value.speedSensitivity,
      DEFAULT_CAMERA.speedSensitivity,
    ),
    lookAhead: asFiniteNumber(value.lookAhead, DEFAULT_CAMERA.lookAhead),
    aimBias: asFiniteNumber(value.aimBias, DEFAULT_CAMERA.aimBias),
    paddingM: asFiniteNumber(value.paddingM, DEFAULT_CAMERA.paddingM),
    circleFit: asFiniteNumber(value.circleFit, DEFAULT_CAMERA.circleFit),
    fitBlend: asFiniteNumber(value.fitBlend, DEFAULT_CAMERA.fitBlend),
    smoothness,
    smoothnessIn: asFiniteNumber(value.smoothnessIn, smoothness),
    minAutoZoom: asFiniteNumber(value.minAutoZoom, DEFAULT_CAMERA.minAutoZoom),
    maxAutoZoom: asFiniteNumber(value.maxAutoZoom, DEFAULT_CAMERA.maxAutoZoom),
  };
}

export function createDefaultDebugDefaults(): DebugDefaultsDocument {
  return {
    version: CURRENT_DEBUG_DEFAULTS_VERSION,
    curve: DEFAULT_CURVE_KIND,
    radiusMeters: pixelsToMeters(MOVEMENT_RADIUS),
    crashHarshness: CRASH_HARSHNESS,
    turnTax: TURN_TAX,
    lines: { trail: true, moveLine: true, prediction: true, windGhost: false },
    wind: { ...DEFAULT_WIND_TUNING },
    previewGates: true,
    showLog: true,
    stickyEdges: true,
    stickyOverflowPx: STICK_OVERFLOW_PX,
    nearMissPx: NEAR_MISS_PX,
    trailLengthMeters: TRAIL_LENGTH_DEFAULT_M,
    trailSpeedGain: TRAIL_SPEED_GAIN_DEFAULT,
    camera: { ...DEFAULT_CAMERA },
  };
}

/**
 * Normalises any supported generation into a current document, or null if the input is not a
 * defaults file. Unknown fields are dropped.
 */
export function migrateDebugDefaults(value: unknown): DebugDefaultsDocument | null {
  if (!isRecord(value)) {
    return null;
  }

  const rawVersion = value.version;

  if (
    typeof rawVersion !== 'number' ||
    rawVersion < OLDEST_SUPPORTED_DEBUG_DEFAULTS_VERSION ||
    rawVersion > CURRENT_DEBUG_DEFAULTS_VERSION
  ) {
    return null;
  }

  const factory = createDefaultDebugDefaults();

  return {
    version: CURRENT_DEBUG_DEFAULTS_VERSION,
    curve: asCurveKind(value.curve),
    radiusMeters: clamp(
      asFiniteNumber(value.radiusMeters, factory.radiusMeters),
      pixelsToMeters(MOVEMENT_RADIUS_MIN),
      pixelsToMeters(MOVEMENT_RADIUS_MAX),
    ),
    crashHarshness: clamp(
      asFiniteNumber(value.crashHarshness, factory.crashHarshness),
      CRASH_HARSHNESS_MIN,
      CRASH_HARSHNESS_MAX,
    ),
    turnTax: clamp(
      asFiniteNumber(value.turnTax, factory.turnTax),
      TURN_TAX_MIN,
      TURN_TAX_MAX,
    ),
    lines: migrateLines(value.lines),
    wind: migrateWind(value.wind),
    previewGates: asBoolean(value.previewGates, factory.previewGates),
    showLog: asBoolean(value.showLog, factory.showLog),
    stickyEdges: asBoolean(value.stickyEdges, factory.stickyEdges),
    stickyOverflowPx: Math.max(
      STICK_OVERFLOW_PX_MIN,
      asFiniteNumber(value.stickyOverflowPx, factory.stickyOverflowPx),
    ),
    nearMissPx: clamp(
      asFiniteNumber(value.nearMissPx, factory.nearMissPx),
      NEAR_MISS_PX_MIN,
      NEAR_MISS_PX_MAX,
    ),
    trailLengthMeters: clamp(
      asFiniteNumber(value.trailLengthMeters, factory.trailLengthMeters),
      TRAIL_LENGTH_MIN_M,
      TRAIL_LENGTH_MAX_M,
    ),
    trailSpeedGain: clamp(
      asFiniteNumber(value.trailSpeedGain, factory.trailSpeedGain),
      TRAIL_SPEED_GAIN_MIN,
      TRAIL_SPEED_GAIN_MAX,
    ),
    camera: migrateCamera(value.camera),
  };
}
